import {
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import {
    CommonRoutes,
    FilterType,
} from 'app/enums/common.enums'
import {
    LongTermInvoicesTableTabs,
} from 'app/enums/domain.enums'
import {
    PersistentKeys,
} from 'app/enums/persistentKeys'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    SkyDataGridColumnDefinition,
} from 'app/shared-components/SkyBaseDataGrid'
import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import {
    SkyNetObjectConfigParams,
} from 'app/shared-components/SkyNetObjectProfile'
import {
    ContractCurrency,
    InvoiceDesignation,
    InvoiceStatus,
    InvoiceType,
} from 'app/enums/label.enums'
import longTermLeaseInvoiceRequests
    from 'app/requests/LongTermLeaseInvoice/LongTermLeaseInvoice.requests'
import {
    LongTermLeaseInvoiceFilterResponse,
} from 'app/requests/LongTermLeaseInvoice/LongTermLeaseInvoice.types'
import {
    LongTermLeaseInvoiceTable,
} from 'app/requests/LongTermLease/LongTermLease.types'
import MONTHS_BY_NUMBER from 'app/utils/monthsByNumber'

const ltLinkRenderCell = (getLink: (params: unknown) => string) => {
    return (cell: GridRenderCellParams) => {
        const {
            row: {
                longTermLeaseId, longTermLeaseNumber,
            },
        } = cell as { row: LongTermLeaseInvoiceFilterResponse }

        return (
            <SkyNetLink
                text={longTermLeaseNumber}
                type={TypeLink.EXTERNAL}
                size={SizeLink.S}
                link={getLink(longTermLeaseId)}
            />
        )
    }
}

const allInvoicesColumns = ({
    getLongTermLeaseLink,
}: {
    getLongTermLeaseLink: (params: unknown) => string
}): SkyDataGridColumnDefinition<LongTermLeaseInvoiceFilterResponse>[] => {
    return [
        {
            name: 'longTermLeaseInvoiceNumber',
            labelKey: 'CONTRACT',
            reduced: true,
            renderCell: ltLinkRenderCell(getLongTermLeaseLink),
        },
        {
            name: 'customerExecutionLocationName',
            filterField: 'customerExecutionLocationName',
            filterType: FilterType.Array,
            labelKey: 'CUSTOMER',
            reduced: true,
        },
        {
            name: 'billingCompanyLocationName',
            filterField: 'billingCompanyLocationName',
            filterType: FilterType.Array,
            labelKey: 'BILLING_COMPANY',
        },
        {
            name: 'pricingAccountCompanyName',
            filterField: 'pricingAccountCompanyName',
            filterType: FilterType.Array,
            labelKey: 'PRICING_ACCOUNT',
        },
        {
            name: 'invoiceDesignation',
            filterField: 'invoiceDesignation',
            filterType: FilterType.Array,
            componentName: 'EnumValue',
            allowedValuesEnum: InvoiceDesignation,
            labelKey: 'INVOICE_DESIGNATION',
        },
        {
            name: 'invoiceType',
            filterField: 'invoiceType',
            filterType: FilterType.Array,
            componentName: 'EnumValue',
            allowedValuesEnum: InvoiceType,
            labelKey: 'INVOICE_TYPE',
        },
        {
            name: 'invoiceDate',
            componentName: 'DateTimeCell',
            labelKey: 'INVOICE_DATE',
        },
        {
            name: 'invoiceStatus',
            filterField: 'invoiceStatus',
            filterType: FilterType.Array,
            componentName: 'EnumValue',
            allowedValuesEnum: InvoiceStatus,
            labelKey: 'INVOICE_STATUS',
        },
        {
            name: 'invoiceTitle',
            labelKey: 'INVOICE_TITLE',
        },
        {
            name: 'billedMonth',
            renderCell: (params: GridRenderCellParams<LongTermLeaseInvoiceTable>) => {
                return MONTHS_BY_NUMBER[params.row.billedMonth]
            },
            labelKey: 'BILLED_MONTH',
        },
        {
            name: 'billedYear',
            labelKey: 'BILLED_YEAR',
        },
        {
            name: 'billedAmount',
            labelKey: 'BILLED_AMOUNT',
        },
        {
            name: 'currency',
            filterField: 'currency',
            filterType: FilterType.Array,
            labelKey: 'CURRENCY',
            componentName: 'EnumValue',
            allowedValuesEnum: ContractCurrency,
        },
    ]
}

const blToBeCreatedColumns = ({
    getLongTermLeaseLink,
}: {
    getLongTermLeaseLink: (params: unknown) => string
}): SkyDataGridColumnDefinition<LongTermLeaseInvoiceFilterResponse>[] => {
    return [
        {
            name: 'longTermLeaseInvoiceNumber',
            labelKey: 'CONTRACT',
            reduced: true,
            renderCell: ltLinkRenderCell(getLongTermLeaseLink),
        },
        {
            name: 'customerExecutionLocationName',
            filterField: 'customerExecutionLocationName',
            filterType: FilterType.Array,
            labelKey: 'CUSTOMER',
            reduced: true,
        },
        {
            name: 'billingCompanyLocationName',
            filterField: 'billingCompanyLocationName',
            filterType: FilterType.Array,
            labelKey: 'BILLING_COMPANY',
        },
        {
            name: 'pricingAccountCompanyName',
            filterField: 'pricingAccountCompanyName',
            filterType: FilterType.Array,
            labelKey: 'PRICING_ACCOUNT',
        },
        {
            name: 'billedMonth',
            labelKey: 'BILLED_MONTH',
        },
        {
            name: 'billedYear',
            labelKey: 'BILLED_YEAR',
        },
    ]
}

const LongTermInvoicesConfig: SkyNetObjectConfigParams<LongTermLeaseInvoiceFilterResponse> = {
    persistentKey: PersistentKeys.FinanceLongTermInvoicesTable,
    formViews: [],
    uniqField: 'id',
    config: [
        {
            url: CommonRoutes.All,
            name: 'ALL_INVOICES',
            columns: allInvoicesColumns,
            requestKey: RequestKeys.getLongTermLeaseInvoiceFilterRequest,
            request: longTermLeaseInvoiceRequests.filterRequestAll(),
        },
        {
            url: LongTermInvoicesTableTabs.BlToBeCreated,
            name: 'BL_TO_BE_CREATED',
            columns: blToBeCreatedColumns,
            requestKey: RequestKeys.getLongTermLeaseInvoiceFilterRequest,
            request: longTermLeaseInvoiceRequests.filterRequestAll({
                params: {
                    data: {
                        includeFilters: {
                            invoiceStatus: [InvoiceStatus.CREATED],
                        },
                    },
                },
            }),
        },
    ],
}

export default LongTermInvoicesConfig
